import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, Zap } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { formatCurrency } from '@/lib/format.js';
import type { CashSession } from '@/lib/api/cash-sessions.js';

type PosTopBarProps = {
  storeName: string;
  cashSession: CashSession | null | undefined;
  onOpenQuoteSearch: () => void;
};

export function PosTopBar({ storeName, cashSession, onOpenQuoteSearch }: PosTopBarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [menuOpen, setMenuOpen] = useState(false);
  const [clock, setClock] = useState(() => new Date());
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const cashierName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Staff';

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4">
      <div className="flex items-center gap-3">
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            <Menu className="h-4 w-4" />
            POS Menu
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
          {menuOpen ? (
            <div className="absolute left-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
              <Link to="/pos" className="block px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                POS Sell
              </Link>
              <Link to="/sales?date=today" className="block px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                Sales History
              </Link>
              <Link to="/tickets" className="block px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                Manage Tickets
              </Link>
              <Link to="/inquiries" className="block px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                Manage Inquiries
              </Link>
              <Link to="/reports" className="block px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                View Reports
              </Link>
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => {
                  setMenuOpen(false);
                  onOpenQuoteSearch();
                }}
              >
                Price Calculator
              </button>
              <Link to="/settings/pos" className="block px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setMenuOpen(false)}>
                POS Settings
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      <div className="text-center">
        <div className="text-sm font-bold text-slate-950">{storeName}</div>
        <div className="text-xs tabular-nums text-slate-500">
          {clock.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Link
          to="/sales?date=today"
          className="hidden rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:inline-flex"
        >
          Sales History
        </Link>
        <div className="hidden text-right sm:block">
          <div className="text-xs text-slate-500">Cashier</div>
          <div className="text-sm font-semibold text-slate-900">{cashierName}</div>
        </div>
        <div className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-800 ring-1 ring-green-200">
          {cashSession
            ? `🟢 ${formatCurrency(cashSession.summary.expectedCash)} in drawer`
            : '🟡 No cash session'}
        </div>
        <button
          type="button"
          onClick={() => navigate('/tickets/quick')}
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-sm font-bold text-white hover:bg-accent/90"
        >
          <Zap className="h-4 w-4" />
          Quick Ticket
        </button>
      </div>
    </header>
  );
}
